import React, { useRef, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Link } from "react-router-dom";
import PropTypes from 'prop-types';
import SVG from 'react-inlinesvg';
import _ from 'lodash';

import * as teacherActions from '@app/actions/teachers';
import Api from '@app/api';
import medalIcon from '@icons/reward/medal';
import cupIcon from '@src/images/landing/cup';
import downloadIcon from '@icons/download/white';
import certificateIcon from '@icons/reward/certificate';
import starsIcon from '@icons/reward/stars';
import questionIcon from '@icons/reward/question';
import teacherRoutes from '@app/routes/teacherRoutes';
import ClassesContainer from '@app/containers/teachers/Classes';
import ScoringPopup from "./components/ScoringPopup";
import Loader from '@app/components/ui/placeholders/Loader';
import useDiplomasStatistics from '@app/hooks/useDiplomasStatistics';
import CertificatePreviewModal from "@app/components/shared/CertificatePreviewModal";

const OpenScoringPopupBtn = ({ onClick }) => {
  return (
    <div className="rewards-stats__learn-rewards" onClick={onClick}>
      <div className="rewards-stats__learn-rewards-icon">
        <SVG src={questionIcon} width="90" height="90" />
      </div>
      <div className="rewards-stats__learn-rewards-label">
        LEARN ABOUT REWARDS
      </div>
    </div>
  );
};

OpenScoringPopupBtn.propTypes = {
  onClick: PropTypes.func.isRequired
};

const ContentVariant = {
  'v1': {
    icon: certificateIcon,
    title: 'You Have a New Certificate!',
    description: <>We appreciate your commitment to the success of your students.</>,
    Button: ({ handleOpenPreviewModal }) => {
      return (
        <div className="rewards-certificate__banner-positive-btn">
          <button className="btn" onClick={handleOpenPreviewModal} style={{backgroundColor: '#9965F4'}}>
            OPEN
          </button>
        </div>
      );
    },
  },
  'v2': {
    icon: starsIcon,
    title: 'Prepare for the Next Olympiad!',
    description: <>Your students didn't participate this time. Solve the tasks together to get ready for the next Olympiad!</>,
    Button: ({ to }) => (
      <div className="rewards-certificate__banner-positive-btn">
        <Link to={to} className="rewards-stats__tasks-btn">
          VIEW TASKS
        </Link>
      </div>
    ),
  }
};

const RewardsBlock = ({ event, classes, boundActions, certPreviewLink }) => {
  const [loading, setLoading] = useState(true);
  const [certificatePath, setCertificatePath] = useState(null);
  const [isAtLeastOneStudentParticipated, setIsAtLeastOneStudentParticipated] = useState(false);
  const { data: diplomasStatistics, loading: diplomasStatisticsLoading, error } = useDiplomasStatistics();
  const scoringPopup = useRef(null);
  const previewModal = useRef(null);

  const firstGroup = _.first(_.values(classes.objects));
  const firstGroupId = firstGroup?.object?.id;

  useEffect(() => {
    const fetchData = async () => {
      if (firstGroupId && event?.afterMainTour) {
        try {
          const path = certPreviewLink
          setCertificatePath(path);
        } catch (error) {
          console.error('Error fetching diplomas:', error);
        } finally {
          setLoading(false);
        }
      }
    };

    fetchData();
  }, [firstGroupId, event?.afterMainTour]);

  useEffect(() => {
    if (diplomasStatistics && diplomasStatistics.total) {
      setIsAtLeastOneStudentParticipated(true);
    }
  }, [diplomasStatistics, diplomasStatisticsLoading]);

  if (!event?.afterMainTour || (event?.afterMainTour && !event?.isAwardsAvailable)) {
    return null;
  }

  if (loading || diplomasStatisticsLoading) {
    return (
      <div className="rewards-block">
        <Loader />
      </div>
    )
  }

  const handleOpenPopup = () => {
    scoringPopup.current.open();
  };

  const handleClosePopup = () => {
    scoringPopup.current.close();
  };

  const handleOpenPreviewModal = () => {
    previewModal.current.open();
  };

  const handleClosePreviewModal = () => {
    previewModal.current.close();
  };

  const content = ContentVariant[isAtLeastOneStudentParticipated ? 'v1' : 'v2'];
  const BannerBtn = content.Button;

  return (
    <div className="rewards-block">
      <div className="rewards-certificate">
        <SVG src={content.icon} className="rewards-certificate__icon"/>
        <h2 className="rewards-certificate__title">
          {content.title}
        </h2>
        <p className="rewards-certificate__description">
          {content.description}
        </p>
        <BannerBtn
          to={teacherRoutes.Tasks.path}
          handleOpenPreviewModal={handleOpenPreviewModal}
        />
        {isAtLeastOneStudentParticipated && (
          <div className="rewards-certificate__note">
            Students who qualify for the Final Round will be invited to participate through their schools and chat messages.
          </div>
        )}
      </div>
      <div className="rewards-stats">
        <h3 className="rewards-stats__title">
          Certificates of Your Students
        </h3>
        <div className="rewards-stats__list">
          <div className="rewards-stats__item">
            <div className="rewards-stats__icon">
              <SVG src={cupIcon}/>
            </div>
            <span className="rewards-stats__label">Achievement:</span>
            <span className="rewards-stats__value">{diplomasStatistics.achievement}</span>
          </div>
          <div className="rewards-stats__item">
            <div className="rewards-stats__icon">
              <SVG src={medalIcon}/>
            </div>
            <span className="rewards-stats__label">Merit:</span>
            <span className="rewards-stats__value">{diplomasStatistics.merit}</span>
          </div>
          <div className="rewards-stats__item">
            <div className="rewards-stats__icon">
              <SVG src={certificateIcon}/>
            </div>
            <span className="rewards-stats__label">Participation:</span>
            <span className="rewards-stats__value">{diplomasStatistics.participation}</span>
          </div>
        </div>
        {isAtLeastOneStudentParticipated && (
          <Link to={teacherRoutes.Leaders.path} className="rewards-stats__rankings-btn">
            SEE RANKINGS
          </Link>
        )}
        <OpenScoringPopupBtn onClick={handleOpenPopup}/>
        <CertificatePreviewModal modal={previewModal} src={certificatePath} onClose={handleClosePreviewModal} />
        <ScoringPopup
          modal={scoringPopup}
          onClose={handleClosePopup}
        />
      </div>
    </div>
  )
    ;
};

RewardsBlock.propTypes = {
  event: PropTypes.shape({
    id: PropTypes.string.isRequired,
    afterMainTour: PropTypes.bool.isRequired
  }).isRequired,
  classes: PropTypes.shape({
    objects: PropTypes.object.isRequired
  }).isRequired,
  boundActions: PropTypes.shape({
    fetchDiplomasIfNeeded: PropTypes.func.isRequired
  }).isRequired
};

const Rewarding = ({event, boundActions, certPreviewLink}) => {
  return (
    <ClassesContainer
      ChildComponent={(props) => (
        <RewardsBlock
          {...props}
          event={event}
          boundActions={boundActions}
          certPreviewLink={certPreviewLink}
        />
      )}
    />
  );
};

Rewarding.propTypes = {
  event: PropTypes.shape({
    id: PropTypes.string.isRequired,
    afterMainTour: PropTypes.bool.isRequired
  }).isRequired,
  boundActions: PropTypes.shape({
    fetchDiplomasIfNeeded: PropTypes.func.isRequired
  }).isRequired
};

const mapStateToProps = (state) => ({
  event: state.global.event,
  certPreviewLink: state.session.user.preview,
});

const mapDispatchToProps = (dispatch) => ({
  boundActions: bindActionCreators(teacherActions, dispatch)
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(Rewarding);